import { Injectable } from "@angular/core";
import { Observable, throwError, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { HttpCemex, CmxHttpOptions } from "@cemex-core/angular-services-v8";
import { ILegalEntity } from "@cemex-core/types-v2";
import { Company } from "../models/company.model";

import { LegalEntityResponse, ResponseLegalEntityS1, ResponseMyLegalEntities } from "./../models/legalEntity.dto";
import { EndpointService } from "./../../services/helpers/endpoint.service";
import { Jobsite } from "../models/jobsite.model";
import { Customer } from "./../models/customer.model";
import { Payer } from "./../models/payer.model";
import { AppConfigurationService } from "../../../cemexng2components/components/app-configuration";
import { IamAuthService } from '@cemex-core/msal-services-v1';

@Injectable()
export class CoreService {
  constructor(
    private http: HttpCemex,
    private endointService: EndpointService,
    private features: AppConfigurationService,
    private iamService: IamAuthService
  ) {}

  /**
   * @description Get the entire legal entity data of an user.
   * @returns An Observable that returns the legal entity response object.
   * @observation the api gobernance is DS04
   */
  public getLegalEntity(): Observable<LegalEntityResponse> {
    const endpoint = "legalentity";
    let version = "v2";

    if (this.features.isEnabled("ipc-company-selector.version-3")) {
      version = "v3";
    }

    return this.http.get(this.endointService.getUrl(version, "arm", endpoint)).pipe(
      map((response: LegalEntityResponse) => {
        return response;
      }),
      catchError((error: any) => throwError(error.error || "Request from Server error"))
    );
  }

  /**
   * @description Get the entire legal entity data of an user.
   * @param companyCode The identifier code of the customer (optional)
   * @returns An Observable that returns the legal entity response object.
   * @observation the api gobernance is DS05
   */
  public getLegalEntityCustomers(companyCode?: string): Observable<Customer[]> {
    const endpoint = this.endointService.getUrl("v5", "sm", "mylegalentities");
    let options: CmxHttpOptions = new CmxHttpOptions();
    if (companyCode) {
      options = {
        params: { companyCode: companyCode },
      } as CmxHttpOptions;
    }

    return this.http.get(endpoint, options).pipe(
      catchError((error) => throwError(endpoint + " FAILED.")),
      map((response: ResponseMyLegalEntities) => {
        const result = this.MyLegalEntityToCustomer(response.legalEntities);
        return result;
      })
    );
    // map((response) => {
    //     const aux = response as ResponseMyLegalEntities;
    //     const result = this.MyLegalEntityToCustomer(aux.legalEntities);
    //     return result;
    // })
    // .catch((error: any) => Observable.throw(error.error || 'Request from Server error'));
  }

  /**
   * @description Get the jobsites list of a specific company and customer
   * @param customerCode The identifier code of a customer
   * @param companyCode The identifier code of a company
   * @returns An observable that returns an array of jobsites
   * @observation the api gobernance is DS04
   */
  public getLegalEntityJobsites(customerCode: string, companyCode: string, countryCode: string): Observable<Jobsite[]> {
    const endpoint = this.endointService.getUrl("v2", "arm", "legalentityjobsites");

    const options = {
      params: {
        customerCode: customerCode,
        companyCode: companyCode,
        countryCode: countryCode,
      },
    } as CmxHttpOptions;
    return this.http.get(endpoint, options).pipe(
      catchError((error) => {
        return throwError("GET Request failed...");
      }),
      map((response: LegalEntityResponse) => {
        return response.responseLegalEntity.jobSite;
      })
    );
  }
  // TODO: need to change that API url to a new one
  public getLegalEntityJobsitesV3(
    customerCode: string,
    companyCode: string,
    countryCode: string
  ): Observable<Jobsite[]> {
    const endpoint = this.endointService.getUrl("v3", "arm", "legalentityjobsites");

    const options = {
      params: {
        customerCode: customerCode,
        companyCode: companyCode,
        countryCode: countryCode,
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      catchError((error) => {
        return throwError("GET Request failed...");
      }),
      map((response: LegalEntityResponse) => {
        return response.responseLegalEntity.jobSite;
      })
    );
  }

  /**
   * @description Get the companies list of a specific legal entity
   * @param customerCode The identifier code of a legal entity
   * @returns An observable that returns an array of companies
   * @observation the api gobernance is DS04
   */
  public getLegalEntityByCustomer(customerCode: string, countryCode: string): Observable<Company[]> {
    const endpoint = "legalentity?customerCode=" + customerCode + "&countryCode=" + countryCode;
    let version = "v2";

    if (this.features.isEnabled("ipc-company-selector.version-3")) {
      version = "v3";
    }

    return this.http.get(this.endointService.getUrl(version, "arm", endpoint)).pipe(
      catchError((error) => {
        console.error(error);
        return throwError("GET Request failed...");
      }),
      map((response: LegalEntityResponse) => {
        return response.responseLegalEntity.company;
      })
    );
  }

  public getLegalEntityByCompany(companyCode: string): Observable<Customer[]> {
    const endpoint = "legalentitycustomers";

    const options = {
      params: {
        companyCode: companyCode,
      },
    } as CmxHttpOptions;

    return this.http.get(this.endointService.getUrl("v2", "arm", endpoint), options).pipe(
      map((response: LegalEntityResponse) => {
        return response.responseLegalEntity.customer;
      }),
      catchError((error: any) => throwError(error.error || "Request from Server error"))
    );
  }

  /**
   * @description Get the payers associated with a customer and/or company.
   * @param customerCode The legal entity type code.
   * @param companyCode The company code.
   * @param countryCode The country code of the selected legal entity.
   */
  public getLegalEntityPayers(customerCode: string, companyCode: string, countryCode?: string): Observable<Payer[]> {
    const endpoint = this.endointService.getUrl("v2", "arm", "legalentitypayers");

    const options = {
      params: {
        customerCode: customerCode,
        companyCode: companyCode,
        countryCode: countryCode,
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      catchError((error) => {
        return throwError("GET Request failed...");
      }),
      map((response: any) => {
        return response.payer as Payer[];
      })
    );
  }

  /**
   * @description Wrapper of legalentity model from DS05 to Customer model from DS04
   * @param data list of customers
   * @returns An observable that returns an array of customers
   * @observation the api gobernance is DS04
   */
  private MyLegalEntityToCustomer(data: ResponseLegalEntityS1[]): Customer[] {
    const result: Customer[] = [];
    data.forEach((item) => {
      result.push({
        countryCode: item.countryCode,
        customerCode: item.legalEntityTypeCode,
        customerId: item.legalEntityId,
        customerDesc: item.legalEntityDesc,
        address: item.address,
        legalEntityType: item.legalEntityType,
        links: item.links,
      });
    });

    return result;
  }
}
